import { API_URL } from "@/lib/api";

export const FAIL_TEXT = "That took too long. Try again or narrow the date range.";

// Appends to the in-flight assistant bubble (creating it if the last message is the user's).
export function withAssistant(msgs, fn) {
  const last = msgs[msgs.length - 1];
  if (last && last.role === "assistant" && last.pending) {
    return [...msgs.slice(0, -1), fn(last)];
  }
  return [...msgs, fn({ role: "assistant", content: "", pending: true, timestamp: new Date().toISOString() })];
}

// POST /ai/chat/stream and dispatch SSE events to handlers. Rejects on network/HTTP error.
export async function streamBrain(body, handlers) {
  const token = localStorage.getItem("kazo_token");
  const res = await fetch(`${API_URL}/ai/chat/stream`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let finished = false;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const ev = (block.match(/^event: (.*)$/m) || [])[1];
      const data = (block.match(/^data: (.*)$/m) || [])[1];
      if (!ev || !data) continue;
      const parsed = JSON.parse(data);
      if (ev === "done") finished = true;
      handlers[ev]?.(parsed);
    }
  }
  if (!finished) throw new Error("stream ended early");
}

const TOOL_LABELS = {
  sales_summary: "Reading sales data", get_overall_kpis: "Reading headline KPIs", run_aggregation: "Analysing the full data",
  export_csv: "Preparing your CSV", get_data_dictionary: "Checking available data", customer_lookup: "Looking up customers",
  customer_search: "Looking up customers", top_churning_customers: "Finding customers at risk", store_performance: "Reading store performance",
  city_performance: "Reading city performance", top_skus: "Reading product sales", tier_distribution: "Reading loyalty tiers",
  rfm_segments: "Reading customer segments", campaign_leaderboard: "Reading campaign results", coupon_performance: "Reading coupon results",
  nps_summary: "Reading customer feedback", communication_log_summary: "Reading message history",
};

// Friendly, storage-agnostic label for a tool name (never shows internal names).
export function toolLabel(name) {
  if (!name) return "Working";
  return TOOL_LABELS[name] || name.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

// Strip internal wording (database names, raw tool ids) from status lines.
export function friendlyStatus(text) {
  if (!text) return text;
  return String(text)
    .replace(/Scanning data \(([a-z_]+)\)/g, (_, t) => toolLabel(t))
    .replace(/Calling MongoDB tools/gi, "Calling Fundle Brain")
    .replace(/\bMongo(DB)?\b/gi, "data")
    .replace(/\b([a-z]+_[a-z_]+)\b/g, (m) => (TOOL_LABELS[m] ? toolLabel(m) : m));
}
